/**
 * Pure UI state for a run: reducers over worker messages plus formatting
 * helpers for timeline rows. See docs/ui-design.md §3 and §9.
 *
 * Revealed code layers (Phase 3) live beside the events; filters and gutter
 * marks arrive in later phases.
 */

import { type CanaryRegistry, EMPTY_REGISTRY } from "../canary";
import { LONG_DELAY_MS } from "../clock";
import {
  type AuditEvent,
  type Category,
  type ConsoleEntry,
  type GlobalChange,
  type Layer,
  previewValue,
  type SerializedValue,
  type SourceSite,
} from "../events";
import type { RunStats, WorkerMessage } from "../protocol";

export type RunStatus = "idle" | "running" | "done" | "failed" | "cancelled";

export interface TimelineState {
  runId: string | null;
  status: RunStatus;
  /** Ordered by `seq`, without duplicates. */
  events: AuditEvent[];
  /** Revealed dynamic-code layers, ordered by id (= editor tab order). */
  layers: Layer[];
  console: ConsoleEntry[];
  stats: RunStats | null;
  limitsHit: string[];
  /** The engine-internal failure message, if the run failed. */
  error: string | null;
  /** The final document's HTML, for the Page preview. */
  html: string | null;
  /** The run's canaries, from `done` (empty until then); input to core/analysis. */
  canaries: CanaryRegistry;
  /** The end-of-run global/intrinsic diff, from `done`; input to core/analysis. */
  globalDiff: GlobalChange[];
}

export const initialTimeline: TimelineState = {
  runId: null,
  status: "idle",
  events: [],
  layers: [],
  console: [],
  stats: null,
  limitsHit: [],
  error: null,
  html: null,
  canaries: EMPTY_REGISTRY,
  globalDiff: [],
};

/** Begins a new run, discarding the previous one. */
export const startRun = (_state: TimelineState, runId: string): TimelineState => ({
  ...initialTimeline,
  runId,
  status: "running",
});

/** Marks the current run as cancelled; later messages for it are ignored. */
export const cancelRun = (state: TimelineState): TimelineState =>
  state.status === "running" ? { ...state, status: "cancelled" } : state;

/**
 * Merges a batch into an event log ordered by `seq`. Events already in the
 * log win over duplicates. Returns `events` itself when nothing is new.
 */
export function applyEvents(events: AuditEvent[], incoming: AuditEvent[]): AuditEvent[] {
  const last = events.at(-1)?.seq ?? -1;
  const fresh = incoming.filter((e) => e.seq > last);
  // Batches arrive in order in practice, so the fast path is a plain append.
  if (
    fresh.length === incoming.length &&
    fresh.every((e, i) => e.seq > (fresh[i - 1]?.seq ?? -1))
  ) {
    return fresh.length === 0 ? events : [...events, ...fresh];
  }
  const bySeq = new Map<number, AuditEvent>();
  for (const e of incoming) if (!bySeq.has(e.seq)) bySeq.set(e.seq, e);
  for (const e of events) bySeq.set(e.seq, e);
  if (bySeq.size === events.length) return events;
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/**
 * Merges newly revealed layers into a list ordered by id. Layers already in
 * the list win over duplicates. Returns `layers` itself when nothing is new.
 */
export function applyLayers(layers: Layer[], incoming: Layer[]): Layer[] {
  const known = new Set(layers.map((l) => l.id));
  const fresh: Layer[] = [];
  for (const layer of incoming) {
    if (known.has(layer.id)) continue;
    known.add(layer.id);
    fresh.push(layer);
  }
  if (fresh.length === 0) return layers;
  return [...layers, ...fresh].sort((a, b) => a.id - b.id);
}

/** A layer's editor-tab and tree label: `layer 2 · Function`. */
export const layerLabel = (layer: Layer): string => `layer ${layer.id} · ${layer.kind}`;

export interface LayerNode {
  layer: Layer;
  children: LayerNode[];
}

/** The "Code layers" tree: layers nested under the layer that created them. */
export function layerTree(layers: Layer[]): LayerNode[] {
  const nodes = new Map(layers.map((layer) => [layer.id, { layer, children: [] as LayerNode[] }]));
  const roots: LayerNode[] = [];
  for (const node of nodes.values()) {
    const parent = nodes.get(node.layer.parent);
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}

/**
 * How deep each layer is nested below user code (layer 0 → 0). Parents always
 * have smaller ids, so one pass in id order suffices.
 */
export function layerDepths(layers: Layer[]): ReadonlyMap<number, number> {
  const depths = new Map([[0, 0]]);
  for (const layer of layers) depths.set(layer.id, (depths.get(layer.parent) ?? 0) + 1);
  return depths;
}

/**
 * How far to indent an event's timeline row: its layer's depth, so events
 * from eval'd code sit under the codegen row that revealed it. A layer not yet
 * announced counts as one level deep.
 */
export const rowIndent = (event: AuditEvent, depths: ReadonlyMap<number, number>): number =>
  depths.get(event.layer) ?? (event.layer === 0 ? 0 : 1);

/**
 * The editor tab to show: `selected` (a layer id; 0 = main.js) if it still
 * exists, else main.js. A new run clears the layers, which resets the tab.
 */
export const resolveTab = (selected: number, layers: Layer[]): number =>
  selected === 0 || layers.some((l) => l.id === selected) ? selected : 0;

/** Folds one worker message into the state. Messages for other runs are ignored. */
export function applyWorkerMessage(state: TimelineState, message: WorkerMessage): TimelineState {
  if (state.status !== "running" || message.type === "ready") return state;
  if (message.type === "error") {
    return message.runId === undefined || message.runId === state.runId
      ? { ...state, status: "failed", error: message.message }
      : state;
  }
  if (message.runId !== state.runId) return state;
  switch (message.type) {
    case "events": {
      const events = applyEvents(state.events, message.events);
      return events === state.events ? state : { ...state, events };
    }
    case "layers": {
      const layers = applyLayers(state.layers, message.layers);
      return layers === state.layers ? state : { ...state, layers };
    }
    case "console":
      return message.entries.length === 0
        ? state
        : { ...state, console: [...state.console, ...message.entries] };
    case "done":
      return {
        ...state,
        status: "done",
        stats: message.stats,
        limitsHit: message.limitsHit,
        html: message.html ?? null,
        canaries: message.canaries ?? EMPTY_REGISTRY,
        globalDiff: message.globalDiff ?? [],
      };
  }
}

/** A one-line summary for a timeline row. */
export function describeEvent(event: AuditEvent): string {
  if (event.network) {
    const request = `${event.network.method} ${event.network.url}`;
    return event.node ? `${request} (${event.node})` : request;
  }
  const module = event.codegen?.module;
  if (module) {
    const specifier = JSON.stringify(module.specifier);
    return module.dynamic
      ? `import(${specifier})`
      : `import { ${module.names.join(", ")} } from ${specifier}`;
  }
  const args = event.args.map((a) => previewValue(a, 80));
  const newLayer = event.codegen?.newLayer;
  if (newLayer !== undefined) {
    return `${describeCodegenSink(event, args)} → layer ${newLayer}`;
  }
  if (event.op === "fire" && event.timer) {
    return `delayed trigger: ${event.target}(${args.join(", ")}) after ${formatVirtualTime(event.timer.delay)}`;
  }
  // On a DOM node, name the node rather than its interface.
  const target = event.node
    ? `${event.node}.${event.target.slice(event.target.lastIndexOf(".") + 1)}`
    : event.target;
  switch (event.op) {
    case "call":
    case "insert":
      return `${target}(${args.join(", ")})`;
    case "get":
      return target;
    case "decode":
      return `${target}(${args.join(", ")}) → ${event.result === undefined ? "?" : previewValue(event.result, 80)}`;
    case "set":
      return `${target} = ${args.join(", ")}`;
    default:
      return [event.op, event.target === "run" ? "" : target, ...args]
        .filter((part) => part !== "")
        .join(" ");
  }
}

/**
 * What a codegen row ran, labelled by its sink where the API call alone
 * would mislead (an inline script reads as `body.append("…")`). Written
 * scripts and string timers keep the guest's target, e.g. `window.setTimeout`.
 */
function describeCodegenSink(event: AuditEvent, args: string[]): string {
  const payload = event.codegen?.payload ?? "";
  switch (event.codegen?.sink) {
    case "inline-script":
      return `<script> ${previewValue(payload, 80)}`;
    case "javascript-url":
      return `javascript: ${payload.length > 80 ? `${payload.slice(0, 79)}…` : payload}`;
    default:
      return `${event.target}(${args.join(", ")})`;
  }
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Formats virtual time compactly, to at most two units (`14 d 3 h`, `2 min 5 s`). */
export function formatVirtualTime(ms: number): string {
  if (ms < SECOND) return `${Number(ms.toFixed(1))} ms`;
  if (ms < MINUTE) return `${Number((ms / SECOND).toFixed(1))} s`;
  const [size, unit, nextSize, nextUnit] =
    ms >= DAY
      ? ([DAY, "d", HOUR, "h"] as const)
      : ms >= HOUR
        ? ([HOUR, "h", MINUTE, "min"] as const)
        : ([MINUTE, "min", SECOND, "s"] as const);
  const parts = [`${Math.floor(ms / size)} ${unit}`];
  const rest = Math.floor((ms % size) / nextSize);
  if (rest > 0) parts.push(`${rest} ${nextUnit}`);
  return parts.join(" ");
}

export type TimelineRow =
  | { kind: "event"; event: AuditEvent }
  /** A long virtual-time gap the clock fast-forwarded over. */
  | { kind: "skip"; from: number; to: number };

/** Timeline rows: events, with a time-skip divider before each long gap. */
export function timelineRows(events: AuditEvent[]): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let last = 0;
  for (const event of events) {
    if (event.t - last >= LONG_DELAY_MS) rows.push({ kind: "skip", from: last, to: event.t });
    rows.push({ kind: "event", event });
    last = Math.max(last, event.t);
  }
  return rows;
}

export type TimelineAction =
  | { type: "start"; runId: string }
  | { type: "cancel" }
  | { type: "message"; message: WorkerMessage };

/** The UI store's reducer (docs/ui-design.md §9). */
export function timelineReducer(state: TimelineState, action: TimelineAction): TimelineState {
  switch (action.type) {
    case "start":
      return startRun(state, action.runId);
    case "cancel":
      return cancelRun(state);
    case "message":
      return applyWorkerMessage(state, action.message);
  }
}

/** Short badge text per category; badges always carry text, not just color. */
export const CATEGORY_LABELS: Record<Category, string> = {
  network: "NET",
  dom: "DOM",
  storage: "STORAGE",
  globals: "GLOBALS",
  probe: "PROBE",
  codegen: "CODEGEN",
  timers: "TIMER",
  fingerprint: "FPRINT",
  evasion: "EVASION",
  messaging: "MSG",
  lifecycle: "RUN",
};

const LIMIT_NAMES: Record<string, string> = {
  cpu: "the CPU limit",
  memory: "the memory limit",
  events: "the event limit",
  timers: "the timer limit",
  time: "the virtual-time limit",
};

/** The Console's final status line (docs/ui-design.md §4). */
export function describeStatus(state: TimelineState): string {
  switch (state.status) {
    case "idle":
      return "Ready. Press Run to execute the code in the sandbox.";
    case "running":
      return "Running…";
    case "cancelled":
      return "Stopped.";
    case "failed":
      return `Engine error: ${state.error ?? "unknown"}`;
    case "done": {
      const stats = state.stats ?? { events: 0, cpuMs: 0, virtualMs: 0 };
      const outcome =
        state.limitsHit.length === 0
          ? "finished"
          : `stopped by ${state.limitsHit.map((id) => LIMIT_NAMES[id] ?? `the ${id} limit`).join(" and ")}`;
      const parts = [outcome, `${Math.round(stats.cpuMs)} ms CPU`];
      if (stats.virtualMs > 0) parts.push(`${formatVirtualTime(stats.virtualMs)} virtual`);
      parts.push(`${stats.events} event${stats.events === 1 ? "" : "s"}`);
      return parts.join(" · ");
    }
  }
}

/** A compact source site for timeline rows: `L12:5`, or `layer 2 · L3:1`. */
export function formatSite(site: SourceSite | undefined): string {
  if (!site) return "";
  const position = `L${site.line}:${site.column}`;
  return site.layer === 0 ? position : `layer ${site.layer} · ${position}`;
}

/** A console entry's source, file-style: `main.js:3`, or `layer 2:1` for revealed code. */
export const formatConsoleSite = (site: SourceSite): string =>
  `${site.layer === 0 ? "main.js" : `layer ${site.layer}`}:${site.line}`;

/** Renders console arguments like a browser console: top-level strings print bare. */
export function formatConsoleArgs(args: SerializedValue[]): string {
  return args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      if (arg !== null && typeof arg === "object" && "$str" in arg) {
        return `${arg.$str}… (${arg.length} chars)`;
      }
      return previewValue(arg, 2000);
    })
    .join(" ");
}
